import { emptyToNull, formatDate as pamFormatDate, getCurrentUtcTimestamp } from "@1823-partners/core";

// Re-export 1823-core utilities for existing consumers
// Wrap formatDate to return '' instead of null for backward compatibility
export const formatDate = (date) => pamFormatDate(date) ?? '';
export { getCurrentUtcTimestamp };

export const mapFileDataToOverrides = (fileData, userId, selectedDate = null) => {
  // Define field mapping between common column names and our override fields
  const fieldMapping = {
    'CUSIP': ['CUSIP', 'cusip', 'Cusip'],
    'NAME': ['NAME', 'Name', 'name', 'Security Name', 'SECURITY_NAME'],
    'WAL': ['WAL', 'wal', 'Wal', 'Weighted Average Life'],
    'BOOK_YIELD': ['BOOK_YIELD', 'Book Yield', 'book_yield', 'BookYield', 'Yield'],
    'CREDIT_ENHANCEMENT': ['CREDIT_ENHANCEMENT', 'Credit Enhancement', 'credit_enhancement', 'CreditEnhancement'],
    'THICKNESS': ['THICKNESS', 'Thickness', 'thickness'],
    'NOTES': ['NOTES', 'Notes', 'notes', 'Comments', 'COMMENTS']
  };

  return fileData.map((row, index) => {
    const mappedRow = {};

    // Map fields based on the mapping
    Object.keys(fieldMapping).forEach(targetField => {
      const possibleColumns = fieldMapping[targetField];
      const matchedColumn = possibleColumns.find(col => row.hasOwnProperty(col));

      if (matchedColumn) {
        let value = row[matchedColumn];

        // Convert empty strings to empty for display
        if (value === null || value === undefined) {
          value = '';
        }

        // Special handling for different field types
        if (targetField === 'BOOK_YIELD' && value) {
          // Keep Book Yield as string without % symbol for editing consistency
          if (typeof value === 'string') {
            value = value.replace(/%/g, '').trim();
          } else if (typeof value === 'number') {
            // If it's a decimal (0.06), convert to percentage (6)
            if (value < 1) {
              value = (value * 100).toString();
            } else {
              value = value.toString();
            }
          }
        } else if (['WAL', 'CREDIT_ENHANCEMENT', 'THICKNESS'].includes(targetField) && value && typeof value === 'string') {
          // Handle numeric values
          const numValue = parseFloat(value.replace(/,/g, ''));
          if (!isNaN(numValue)) {
            value = numValue;
          }
        }

        mappedRow[targetField] = value;
      } else {
        mappedRow[targetField] = '';
      }
    });

    // Set system fields
    mappedRow.MODIFIED_BY = userId;
    mappedRow.MODIFIED_DATE = getCurrentUtcTimestamp();

    // Set asofdt to selectedDate in YYYY-MM-DD format if provided
    if (selectedDate) {
      if (selectedDate instanceof Date) {
        mappedRow.ASOFDT = selectedDate.toISOString().split('T')[0];
      } else if (typeof selectedDate === 'string') {
        const dateMatch = selectedDate.match(/(\d{4}-\d{2}-\d{2})/);
        mappedRow.ASOFDT = dateMatch ? dateMatch[1] : selectedDate;
      } else {
        mappedRow.ASOFDT = selectedDate;
      }
    }

    // If no CUSIP provided, generate a temporary one
    if (!mappedRow.CUSIP || mappedRow.CUSIP === '') {
      mappedRow.CUSIP = `imported_row_${index}`;
    }

    // Add temporary ID for tracking new imported rows
    mappedRow._tempId = `imported_${index}_${Date.now()}`;

    return mappedRow;
  });
};

export const mergeFileDataWithExistingOverrides = (fileData, currentRowData, userId, selectedDate = null) => {
  // Define field mapping between common column names and our override fields
  const fieldMapping = {
    'CUSIP': ['CUSIP', 'cusip', 'Cusip'],
    'NAME': ['NAME', 'Name', 'name', 'Security Name', 'SECURITY_NAME'],
    'WAL': ['WAL', 'wal', 'Wal', 'Weighted Average Life'],
    'BOOK_YIELD': ['BOOK_YIELD', 'Book Yield', 'book_yield', 'BookYield', 'Yield'],
    'CREDIT_ENHANCEMENT': ['CREDIT_ENHANCEMENT', 'Credit Enhancement', 'credit_enhancement', 'CreditEnhancement'],
    'THICKNESS': ['THICKNESS', 'Thickness', 'thickness'],
    'NOTES': ['NOTES', 'Notes', 'notes', 'Comments', 'COMMENTS'],
    'ASOFDT': ['ASOFDT', 'asofdt', 'AsOfDt', 'Date', 'DATE']
  };

  // Validation: Check if CUSIP column exists in uploaded file
  const firstRow = fileData[0] || {};
  const cusipColumns = fieldMapping['CUSIP'];
  const hasCusipColumn = cusipColumns.some(col => firstRow.hasOwnProperty(col));

  if (!hasCusipColumn) {
    throw new Error('CUSIP column is required in the uploaded file but was not found. Please ensure your file includes a CUSIP column.');
  }

  // Check for ASOFDT column in uploaded file
  const asoftdColumns = fieldMapping['ASOFDT'];
  const hasAsoftdColumn = asoftdColumns.some(col => firstRow.hasOwnProperty(col));

  // If no ASOFDT column and no selected date, reject the file
  if (!hasAsoftdColumn && !selectedDate) {
    throw new Error('ASOFDT (Date) column is required in the uploaded file when no date is selected in the grid. Please either select a date in the grid or include an ASOFDT column in your file.');
  }

  // Create a map of existing data by CUSIP for quick lookup
  const existingDataMap = new Map();
  currentRowData.forEach(row => {
    if (row.CUSIP) {
      existingDataMap.set(row.CUSIP, row);
    }
  });

  // First, deduplicate the uploaded data by CUSIP, with intelligent field merging
  // Earlier rows take precedence, but non-null values take precedence over null values
  const cusipRowMap = new Map(); // Map of CUSIP -> merged row data
  const duplicatesFound = new Set();
  const rowsWithoutCusip = [];

  fileData.forEach((uploadRow, index) => {
    // Extract CUSIP from the row to check for duplicates
    const cusipColumns = fieldMapping['CUSIP'];
    const matchedCusipColumn = cusipColumns.find(col => uploadRow.hasOwnProperty(col));

    if (matchedCusipColumn) {
      let cusipValue = uploadRow[matchedCusipColumn];
      if (cusipValue !== null && cusipValue !== undefined) {
        cusipValue = String(cusipValue).trim().replace(/['"]/g, '');

        if (cusipValue !== '') {
          if (!cusipRowMap.has(cusipValue)) {
            // First occurrence of this CUSIP - store the row
            cusipRowMap.set(cusipValue, { ...uploadRow });
          } else {
            // Duplicate CUSIP found - merge non-null values
            duplicatesFound.add(cusipValue);
            const existingRow = cusipRowMap.get(cusipValue);

            // Check each field and merge non-null values
            Object.keys(uploadRow).forEach(fieldName => {
              const existingValue = existingRow[fieldName];
              const newValue = uploadRow[fieldName];

              // If existing value is null/empty and new value is not null/empty, use new value
              const isExistingEmpty = existingValue === null || existingValue === undefined || existingValue === '';
              const isNewNotEmpty = newValue !== null && newValue !== undefined && newValue !== '';

              if (isExistingEmpty && isNewNotEmpty) {
                existingRow[fieldName] = newValue;
              }
            });

            cusipRowMap.set(cusipValue, existingRow);
          }
        } else {
          // Include rows with empty CUSIP (they'll be handled by existing validation)
          rowsWithoutCusip.push(uploadRow);
        }
      } else {
        // Include rows without valid CUSIP (they'll be handled by existing validation)
        rowsWithoutCusip.push(uploadRow);
      }
    } else {
      // Include rows without CUSIP column (they'll be handled by existing validation)
      rowsWithoutCusip.push(uploadRow);
    }
  });

  // Convert map back to array, preserving order of first occurrence
  const deduplicatedFileData = [];

  // Add rows with valid CUSIPs (in order they were first encountered)
  fileData.forEach(uploadRow => {
    const cusipColumns = fieldMapping['CUSIP'];
    const matchedCusipColumn = cusipColumns.find(col => uploadRow.hasOwnProperty(col));

    if (matchedCusipColumn) {
      let cusipValue = uploadRow[matchedCusipColumn];
      if (cusipValue !== null && cusipValue !== undefined) {
        cusipValue = String(cusipValue).trim().replace(/['"]/g, '');

        if (cusipValue !== '' && cusipRowMap.has(cusipValue)) {
          deduplicatedFileData.push(cusipRowMap.get(cusipValue));
          cusipRowMap.delete(cusipValue); // Remove to avoid duplicates in output
        }
      }
    }
  });

  // Add rows without CUSIP at the end
  deduplicatedFileData.push(...rowsWithoutCusip);


  const mergedData = [...currentRowData]; // Start with existing data
  const newRows = [];
  const updatedCusips = new Set();
  const fieldChanges = {}; // Track specific field changes for each CUSIP

  deduplicatedFileData.forEach((uploadRow, index) => {
    // Map the uploaded row to our field structure
    const mappedRow = {};

    // First, map all the fields
    Object.keys(fieldMapping).forEach(targetField => {
      const possibleColumns = fieldMapping[targetField];
      const matchedColumn = possibleColumns.find(col => uploadRow.hasOwnProperty(col));

      if (matchedColumn) {
        let value = uploadRow[matchedColumn];

        // Convert null/undefined to empty string
        if (value === null || value === undefined) {
          value = '';
        }

        // Special handling for different field types with proper formatting
        if (targetField === 'CUSIP' && value !== '') {
          // Ensure CUSIP is always a string and trim whitespace
          value = String(value).trim().replace(/['"]/g, ''); // Remove quotes if present
        } else if (targetField === 'BOOK_YIELD' && value && value !== '') {
          // Book Yield: convert to percentage string format (without % symbol)
          if (typeof value === 'string') {
            // Remove % symbol and whitespace, handle common formats
            let cleanValue = value.replace(/%/g, '').trim();
            const numValue = parseFloat(cleanValue);
            if (!isNaN(numValue)) {
              // If it looks like a decimal (0.06), convert to percentage (6.000)
              if (numValue < 1 && numValue > 0) {
                value = (numValue * 100).toFixed(3);
              } else {
                // Format to 3 decimal places for consistency
                value = numValue.toFixed(3);
              }
            } else {
              throw new Error(`Invalid Book Yield value: "${value}". Please provide a valid number or percentage.`);
            }
          } else if (typeof value === 'number') {
            // If it's a decimal (0.06), convert to percentage (6.000)
            if (value < 1 && value > 0) {
              value = (value * 100).toFixed(3);
            } else {
              value = value.toFixed(3);
            }
          } else {
            throw new Error(`Invalid Book Yield value: "${value}". Please provide a valid number or percentage.`);
          }
        } else if (['WAL', 'CREDIT_ENHANCEMENT', 'THICKNESS'].includes(targetField) && value && value !== '') {
          // Numeric fields: ensure proper number format
          let numValue;
          if (typeof value === 'string') {
            // Clean string and convert to number
            const cleanValue = value.replace(/[,\s$]/g, '').trim();
            numValue = parseFloat(cleanValue);
          } else if (typeof value === 'number') {
            numValue = value;
          } else {
            throw new Error(`Invalid ${targetField} value: "${value}". Please provide a valid number.`);
          }

          if (isNaN(numValue)) {
            throw new Error(`Invalid ${targetField} value: "${value}". Please provide a valid number.`);
          }

          // Apply field-specific validation and formatting
          if (targetField === 'THICKNESS') {
            // Thickness should be reasonable (allow negative values, typically -1000 to 1000)
            if (numValue < -1000 || numValue > 1000) {
              throw new Error(`Thickness value "${numValue}" is out of reasonable range (-1000 to 1000).`);
            }
            value = parseFloat(numValue.toFixed(2)); // 2 decimal places
          } else if (targetField === 'CREDIT_ENHANCEMENT') {
            // Credit Enhancement should be reasonable (allow negative values, typically -1000 to 1000)
            if (numValue < -1000 || numValue > 1000) {
              throw new Error(`Credit Enhancement value "${numValue}" is out of reasonable range (-1000 to 1000).`);
            }
            value = parseFloat(numValue.toFixed(1)); // 1 decimal place
          } else if (targetField === 'WAL') {
            // WAL should be positive and reasonable (typically 0-50 years)
            if (numValue < 0 || numValue > 100) {
              throw new Error(`WAL value "${numValue}" is out of reasonable range (0-100 years).`);
            }
            value = parseFloat(numValue.toFixed(1)); // 1 decimal place
          }
        } else if (targetField === 'NAME' && value !== '') {
          // Ensure NAME is a string and trim whitespace
          value = String(value).trim();
        } else if (targetField === 'NOTES' && value !== '') {
          // Ensure NOTES is a string and trim whitespace
          value = String(value).trim();
        }

        mappedRow[targetField] = value;
      }
      // If column doesn't exist in upload, don't set the field (will be handled in merge logic)
    });

    // Get the CUSIP for this row
    const cusip = mappedRow.CUSIP;
    if (!cusip || cusip === '') {
      // Skip rows without CUSIP
      return;
    }

    // Handle ASOFDT
    let asoftdValue = null;
    if (mappedRow.ASOFDT && mappedRow.ASOFDT !== '') {
      // Use the ASOFDT from the file
      asoftdValue = mappedRow.ASOFDT;
    } else if (selectedDate) {
      // Use the selected date from the grid
      if (selectedDate instanceof Date) {
        asoftdValue = selectedDate.toISOString().split('T')[0];
      } else if (typeof selectedDate === 'string') {
        const dateMatch = selectedDate.match(/(\d{4}-\d{2}-\d{2})/);
        asoftdValue = dateMatch ? dateMatch[1] : selectedDate;
      } else {
        asoftdValue = selectedDate;
      }
    }

    // Check if this CUSIP exists in current data
    if (existingDataMap.has(cusip)) {
      // Merge with existing row
      const existingRowIndex = mergedData.findIndex(row => row.CUSIP === cusip);
      if (existingRowIndex !== -1) {
        const existingRow = mergedData[existingRowIndex];
        const mergedRow = { ...existingRow };
        const rowFieldChanges = {};

        // Apply merge logic for each field
        Object.keys(fieldMapping).forEach(targetField => {
          const possibleColumns = fieldMapping[targetField];
          const matchedColumn = possibleColumns.find(col => uploadRow.hasOwnProperty(col));

          if (matchedColumn) {
            // Column exists in upload file
            const uploadValue = mappedRow[targetField];
            if (uploadValue !== '' && uploadValue != null) {
              // Upload has data: check if it's different from current value
              const currentValue = existingRow[targetField];
              if (currentValue !== uploadValue) {
                // Track the field change
                rowFieldChanges[targetField] = {
                  oldValue: currentValue,
                  newValue: uploadValue
                };
                // Overwrite grid data
                mergedRow[targetField] = uploadValue;
              }
            }
            // If upload value is empty, keep current grid data (do nothing)
          }
          // If column doesn't exist in upload, don't replace grid data (do nothing)
        });

        // Only track changes if there were actual field changes
        if (Object.keys(rowFieldChanges).length > 0) {
          fieldChanges[cusip] = rowFieldChanges;
          updatedCusips.add(cusip);

          // Set system fields for tracking changes
          mergedRow.MODIFIED_BY = userId;
          mergedRow.MODIFIED_DATE = getCurrentUtcTimestamp();
          mergedRow.ASOFDT = asoftdValue;

          mergedData[existingRowIndex] = mergedRow;
        }
      }
    } else {
      // New row - doesn't exist in current data
      const newRow = {};

      // Set all override fields, using defaults for missing ones
      ['CUSIP', 'NAME', 'WAL', 'BOOK_YIELD', 'CREDIT_ENHANCEMENT', 'THICKNESS', 'NOTES'].forEach(field => {
        newRow[field] = mappedRow[field] || '';
      });

      // Set system fields
      newRow.MODIFIED_BY = userId;
      newRow.MODIFIED_DATE = getCurrentUtcTimestamp();
      newRow.ASOFDT = asoftdValue;
      newRow._tempId = `imported_${index}_${Date.now()}`;

      newRows.push(newRow);
    }
  });

  // Add new rows to the beginning of the merged data
  const finalData = [...newRows, ...mergedData];

  return {
    mergedData: finalData,
    updatedCount: updatedCusips.size,
    newCount: newRows.length,
    updatedCusips: Array.from(updatedCusips),
    fieldChanges: fieldChanges,
    duplicatesFound: Array.from(duplicatesFound),
    duplicateCount: duplicatesFound.size
  };
};

export const formatOverrideData = (override, asofdt = null) => {
  const formattedOverride = { ...override };

  // Remove the actions column and temporary tracking fields
  delete formattedOverride.actions;
  delete formattedOverride._tempId;

  // Add asofdt field with YYYY-MM-DD format if provided
  if (asofdt) {
    // Ensure asofdt is in YYYY-MM-DD format (not converted to locale string)
    if (asofdt instanceof Date) {
      formattedOverride.ASOFDT = asofdt.toISOString().split('T')[0];
    } else if (typeof asofdt === 'string') {
      // If it's already a string, ensure it's in YYYY-MM-DD format
      const dateMatch = asofdt.match(/(\d{4}-\d{2}-\d{2})/);
      formattedOverride.ASOFDT = dateMatch ? dateMatch[1] : asofdt;
    } else {
      formattedOverride.ASOFDT = asofdt;
    }
  }

  // Convert empty fields to null using 1823-core utility
  const cleanedOverride = emptyToNull(formattedOverride);

  // Convert BOOK_YIELD from percentage string to decimal number for saving
  if (cleanedOverride.BOOK_YIELD !== null && cleanedOverride.BOOK_YIELD !== undefined) {
    if (typeof cleanedOverride.BOOK_YIELD === 'string') {
      const cleanValue = cleanedOverride.BOOK_YIELD.toString().replace(/%/g, '').trim();
      const numValue = Number(cleanValue);
      if (!isNaN(numValue)) {
        cleanedOverride.BOOK_YIELD = numValue / 100; // Convert percentage to decimal
      }
    } else if (typeof cleanedOverride.BOOK_YIELD === 'number') {
      // If it's already a number, check if it needs conversion
      // If the number is greater than 1, it's likely a percentage value that needs conversion
      if (cleanedOverride.BOOK_YIELD > 1) {
        cleanedOverride.BOOK_YIELD = cleanedOverride.BOOK_YIELD / 100;
      }
    }
  }

  return cleanedOverride;
};

